import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { productEndpoints } from "../api/endpoints";
import type { Level, Metric, Product, Segment, Service } from "../types";

type Status = "idle" | "loading" | "success" | "error";

interface LazyProductHierarchyState {
    products: Product[];
    services: Service[];
    levels: Level[];
    segments: Segment[];
    metrics: Metric[];
}

const INITIAL_STATE: LazyProductHierarchyState = {
    products: [],
    services: [],
    levels: [],
    segments: [],
    metrics: [],
};

const mergeUniqueById = <T extends { id: number }>(existing: T[], additions: T[]) => {
    const map = new Map<number, T>();
    existing.forEach((item) => map.set(item.id, item));
    additions.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
};

export const useProductHierarchy = () => {
    const [state, setState] = useState<LazyProductHierarchyState>(INITIAL_STATE);
    const [status, setStatus] = useState<Status>("idle");
    const [error, setError] = useState<string | null>(null);
    const [pendingLoads, setPendingLoads] = useState(0);
    const [pendingProductDetailsLoads, setPendingProductDetailsLoads] = useState(0);
    const [pendingMetricsLoads, setPendingMetricsLoads] = useState(0);
    const [loadedServiceMetricIds, setLoadedServiceMetricIds] = useState<Set<number>>(new Set());

    const loadedProductIds = useRef<Set<number>>(new Set());
    const loadedServiceIds = useRef<Set<number>>(new Set());
    const loadingProductIds = useRef<Set<number>>(new Set());
    const loadingServiceIds = useRef<Set<number>>(new Set());
    const hasLoadedProducts = useRef(false);

    const loadProducts = useCallback(async () => {
        setStatus("loading");
        setError(null);

        try {
            const productsResponse = await productEndpoints.listProducts([]);

            setState((prev) => ({
                ...prev,
                products: productsResponse.map((p) => ({
                    id: p.id,
                    name: p.name,
                })),
            }));
            setStatus("success");
        } catch (err) {
            console.error("Failed to load products", err);
            setStatus("error");
            setError(err instanceof Error ? err.message : "Failed to load products");
        }
    }, []);

    const loadProductDetails = useCallback(
        async (productIds: number[]) => {
            const newProductIds = productIds.filter(
                (id) => !loadedProductIds.current.has(id) && !loadingProductIds.current.has(id)
            );
            if (newProductIds.length === 0) return;

            newProductIds.forEach((id) => loadingProductIds.current.add(id));
            setPendingLoads((c) => c + 1);
            setPendingProductDetailsLoads((c) => c + 1);

            try {
                const servicesPromises = newProductIds.map((productId) => productEndpoints.listServices(productId, ["levels", "levels.segments"]));

                const servicesResponses = await Promise.all(servicesPromises);

                const newServices: Service[] = [];
                const newLevels: Level[] = [];
                const newSegments: Segment[] = [];

                servicesResponses.forEach((services) => {
                    services.forEach((service) => {
                        newServices.push({
                            id: service.id,
                            name: service.name,
                            productId: service.productId,
                            url: service.url,
                        });

                        service.levels?.forEach((level) => {
                            newLevels.push({
                                id: level.id,
                                name: level.name,
                                serviceId: level.serviceId ?? service.id,
                                displayName: level.displayName ?? level.name,
                            });

                            level.segments?.forEach((segment) => {
                                newSegments.push({
                                    id: segment.id,
                                    name: segment.name,
                                    levelId: segment.levelId ?? level.id,
                                    displayName: segment.displayName ?? segment.name,
                                });
                            });
                        });
                    });
                });

                setState((prev) => ({
                    ...prev,
                    services: mergeUniqueById(prev.services, newServices),
                    levels: mergeUniqueById(prev.levels, newLevels),
                    segments: mergeUniqueById(prev.segments, newSegments),
                }));

                newProductIds.forEach((id) => {
                    loadedProductIds.current.add(id);
                    loadingProductIds.current.delete(id);
                });
            } catch (err) {
                console.error("Failed to load product details", err);
                newProductIds.forEach((id) => loadingProductIds.current.delete(id));
            } finally {
                setPendingLoads((c) => Math.max(0, c - 1));
                setPendingProductDetailsLoads((c) => Math.max(0, c - 1));
            }
        },
        []
    );

    const loadServiceMetrics = useCallback(
        async (serviceIds: number[]) => {
            const newServiceIds = serviceIds.filter(
                (id) => !loadedServiceIds.current.has(id) && !loadingServiceIds.current.has(id)
            );
            if (newServiceIds.length === 0) return;

            newServiceIds.forEach((id) => loadingServiceIds.current.add(id));
            setPendingLoads((c) => c + 1);
            setPendingMetricsLoads((c) => c + 1);

            try {
                const metricsPromises = newServiceIds.map((serviceId) => productEndpoints.listMetrics(serviceId));

                const metricsResponses = await Promise.all(metricsPromises);
                const newMetrics: Metric[] = [];

                metricsResponses.forEach((metrics) => {
                    metrics.forEach((metric) => {
                        newMetrics.push({
                            id: metric.id,
                            name: metric.name,
                            showName: metric.showName,
                            category: metric.category,
                            serviceId: metric.serviceId,
                            type: metric.type,
                            metricType: metric.metricType,
                        });
                    });
                });

                setState((prev) => ({
                    ...prev,
                    metrics: mergeUniqueById(prev.metrics, newMetrics),
                }));

                newServiceIds.forEach((id) => {
                    loadedServiceIds.current.add(id);
                    loadingServiceIds.current.delete(id);
                });

                setLoadedServiceMetricIds((prev) => {
                    const next = new Set(prev);
                    newServiceIds.forEach((id) => next.add(id));
                    return next;
                });
            } catch (err) {
                console.error("Failed to load metrics", err);
                newServiceIds.forEach((id) => loadingServiceIds.current.delete(id));
            } finally {
                setPendingLoads((c) => Math.max(0, c - 1));
                setPendingMetricsLoads((c) => Math.max(0, c - 1));
            }
        },
        []
    );

    useEffect(() => {
        if (!hasLoadedProducts.current) {
            hasLoadedProducts.current = true;
            void loadProducts();
        }
    }, [loadProducts]);

    const isLoadingProducts = status === "loading";
    const isLoadingProductDetails = pendingProductDetailsLoads > 0;
    const isLoadingMetrics = pendingMetricsLoads > 0;
    const isLoading = isLoadingProducts || pendingLoads > 0;
    const isError = status === "error";

    const productsById = useMemo(() => {
        return state.products.reduce<Record<number, Product>>((acc, product) => {
            acc[product.id] = product;
            return acc;
        }, {});
    }, [state.products]);

    const servicesById = useMemo(() => {
        return state.services.reduce<Record<number, Service>>((acc, service) => {
            acc[service.id] = service;
            return acc;
        }, {});
    }, [state.services]);

    const levelsById = useMemo(() => {
        return state.levels.reduce<Record<number, Level>>((acc, level) => {
            acc[level.id] = level;
            return acc;
        }, {});
    }, [state.levels]);

    const segmentsById = useMemo(() => {
        return state.segments.reduce<Record<number, Segment>>((acc, segment) => {
            acc[segment.id] = segment;
            return acc;
        }, {});
    }, [state.segments]);

    const metricsById = useMemo(() => {
        return state.metrics.reduce<Record<number, Metric>>((acc, metric) => {
            acc[metric.id] = metric;
            return acc;
        }, {});
    }, [state.metrics]);

    const servicesByProductId = useMemo(() => {
        return state.services.reduce<Record<number, Service[]>>((acc, service) => {
            acc[service.productId] = acc[service.productId] ? [...acc[service.productId], service] : [service];
            return acc;
        }, {});
    }, [state.services]);

    const levelsByServiceId = useMemo(() => {
        return state.levels.reduce<Record<number, Level[]>>((acc, level) => {
            acc[level.serviceId] = acc[level.serviceId] ? [...acc[level.serviceId], level] : [level];
            return acc;
        }, {});
    }, [state.levels]);

    const segmentsByLevelId = useMemo(() => {
        return state.segments.reduce<Record<number, Segment[]>>((acc, segment) => {
            acc[segment.levelId] = acc[segment.levelId] ? [...acc[segment.levelId], segment] : [segment];
            return acc;
        }, {});
    }, [state.segments]);

    const metricsByServiceId = useMemo(() => {
        return state.metrics.reduce<Record<number, Metric[]>>((acc, metric) => {
            acc[metric.serviceId] = acc[metric.serviceId] ? [...acc[metric.serviceId], metric] : [metric];
            return acc;
        }, {});
    }, [state.metrics]);

    return {
        ...state,
        status,
        isLoading,
        isLoadingProducts,
        isLoadingProductDetails,
        isLoadingMetrics,
        isError,
        error,
        loadProducts,
        loadProductDetails,
        loadServiceMetrics,
        loadedServiceMetricIds,
        productsById,
        servicesById,
        levelsById,
        segmentsById,
        metricsById,
        servicesByProductId,
        levelsByServiceId,
        segmentsByLevelId,
        metricsByServiceId,
    };
};
